import * as React from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown, Globe, Mail, Server, LayoutGrid, ArrowRight, Inbox,
  AtSign, HardDrive, Send
} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useEnrollments } from "@/hooks/useEnrollments";
import { PlanMailboxes } from "@/components/account/PlanMailboxes";
import { AddPlanDomainDialog } from "@/components/account/AddPlanDomainDialog";
import { DeleteDomainButton } from "@/components/account/DeleteDomainButton";
import type { EnrollmentDto } from "@/types/api";

export function EnrollmentsList() {
  const { data, isLoading, error } = useEnrollments();

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}><CardContent className="p-5"><Skeleton className="h-14 w-full" /></CardContent></Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
        Couldn't load your plans. Please refresh.
      </CardContent></Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card><CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Inbox className="h-6 w-6" />
        </div>
        <div>
          <p className="font-medium">No plans yet</p>
          <p className="text-sm text-muted-foreground">Pick a plan to start hosting email, sites, or a portfolio.</p>
        </div>
        <Link to="/#services" className="text-sm font-medium text-primary hover:underline">Explore plans →</Link>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-3">
      {data.map(e => <EnrollmentCard key={e.subscription_id} e={e} />)}
    </div>
  );
}

function planIcon(type: string) {
  const t = (type || "").toLowerCase();
  if (t.includes("hosting")) return Server;
  if (t.includes("portfolio")) return LayoutGrid;
  return Mail;
}

function billing(e: EnrollmentDto) {
  const price = e.billing_months === 12 ? e.price_annually
    : e.billing_months === 3 ? e.price_quarterly : e.price_monthly;
  const cadence = e.billing_months === 12 ? "/yr" : e.billing_months === 3 ? "/qtr" : "/mo";
  const sym = e.currency === "USD" ? "$" : `${e.currency} `;
  return { label: `${sym}${price.toFixed(2)}`, cadence };
}

function EnrollmentCard({ e }: { e: EnrollmentDto }) {
  const [open, setOpen] = React.useState(false);
  const Icon = planIcon(e.plan_type);
  const price = billing(e);
  const active = e.status === "active";

  return (
    <Card className="overflow-hidden elevate">
      {/* Header — click to expand */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-4 p-4 text-left transition-colors hover:bg-muted/40 sm:p-5"
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-accentSoft text-primary">
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-base font-semibold text-foreground sm:text-lg">{e.plan_name}</h3>
            <Badge variant={active ? "success" : "outline"} className="capitalize">{e.status}</Badge>
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground sm:text-sm">
            {price.label}<span className="text-muted-foreground">{price.cadence}</span>
            {e.renews_at && <> · renews {new Date(e.renews_at).toLocaleDateString()}</>}
          </p>
        </div>

        {/* Domain count chip */}
        <div className="hidden shrink-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground sm:flex">
          <Globe className="h-3.5 w-3.5 text-muted-foreground" />
          {e.domain_count} of {e.domain_capacity < 0 ? "∞" : e.domain_capacity} domain{e.domain_count === 1 ? "" : "s"}
        </div>

        <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Body — pool + domains */}
      {open && (
        <div className="border-t border-border bg-muted/20">
          <div className="grid gap-2 p-4 sm:grid-cols-3 sm:p-5">
            <PoolStat icon={AtSign} label="Mailboxes"
              used={e.pool.mailboxes_used} total={e.pool.mailboxes_pool} />
            <PoolStat icon={HardDrive} label="Storage" unit=" GB"
              used={e.pool.storage_gb_used} total={e.pool.storage_gb_pool} />
            <PoolStat icon={Send} label="Bulk emails"
              used={e.pool.bulk_emails_used} total={e.pool.bulk_emails_pool} />
          </div>

          <div className="px-4 pb-2 sm:px-5">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Domains in this plan
              </p>
              {hasFreeSlots(e) && (
                <AddPlanDomainDialog
                  subscriptionId={e.subscription_id}
                  planName={e.plan_name}
                  slotsLeft={slotsLeft(e)}
                />
              )}
            </div>
            {e.domains.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">
                No domains attached yet.
              </p>
            ) : (
              <div className="space-y-1.5">
                {e.domains.map(d => (
                  <div
                    key={d.id}
                    className="flex items-center gap-1 rounded-lg border border-border bg-background pr-1 transition-colors hover:border-primary/40"
                  >
                    <Link
                      to={`/domains/${d.id}`}
                      className="group flex min-w-0 flex-1 items-center gap-3 rounded-l-lg px-3 py-2.5 hover:bg-brand-accent/5"
                    >
                      <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">{d.domain_name}</span>
                      <StatusDot status={d.status} />
                      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </Link>
                    <DeleteDomainButton containerId={d.id} domainName={d.domain_name} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Manage email accounts without leaving the Overview */}
          <PlanMailboxes e={e} />

          <div className="flex justify-end p-4 sm:p-5">
            <Link
              to={`/plans/${e.subscription_id}`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
            >
              View plan <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </Card>
  );
}

function PoolStat({
  icon: Icon, label, used, total, unit = ""
}: { icon: React.ComponentType<{ className?: string }>; label: string; used: number; total: number; unit?: string }) {
  const unlimited = total < 0;
  const pct = unlimited || total === 0 ? 0 : Math.min(100, Math.round((used / total) * 100));
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="mt-1 text-sm font-semibold text-foreground">
        {round(used)}{unit} <span className="text-xs font-normal text-muted-foreground">of {unlimited ? "∞" : `${round(total)}${unit}`}</span>
      </p>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function hasFreeSlots(e: EnrollmentDto) {
  const active = (e.status || "").toLowerCase() === "active";
  return active && (e.domain_capacity < 0 || e.domain_count < e.domain_capacity);
}

function slotsLeft(e: EnrollmentDto) {
  return e.domain_capacity < 0 ? -1 : e.domain_capacity - e.domain_count;
}

function StatusDot({ status }: { status: string }) {
  const s = (status || "").toLowerCase();
  const color = s === "active" ? "bg-brand-green" : s.includes("pending") || s.includes("transfer") ? "bg-amber-500" : "bg-muted-foreground";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs capitalize text-muted-foreground">
      <span className={`h-1.5 w-1.5 rounded-full ${color}`} />
      {s || "—"}
    </span>
  );
}

function round(n: number) {
  return Math.round((n + Number.EPSILON) * 10) / 10;
}
